import { fetchWithHeaders, throwError } from '@sendible/common';
import { InfiniteData, MutationFunction, QueryFunction, useMutation, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { mediasLoadedInfiniteScroll } from '../pages/MediaLibrary/pendoEvents';

function unwrapApiResult<TData>(response: unknown): TData {
  if (typeof response === 'object' && response !== null && 'result' in response) {
    return (response as { result: TData }).result;
  }

  return response as TData;
}

export function useQueryWithAccessToken<TData>(
  [endpoint, params]: DLQueryKey,
  options?: UseQueryOptionsWithGenerics<TData>,
  useErrorBoundary = false
) {
  const [sharedState] = useBridgeContext();

  const user = sharedState?.user;

  let credentials: object | undefined;
  let isEnabled = true;

  if (!user) {
    isEnabled = false;
    credentials = {};
  } else if (user.isSwitchedUser) {
    credentials = { username: user.username, logged_in_access_token: user.accessToken };
  } else {
    credentials = { access_token: user.accessToken };
  }

  const newQueryKey: DLQueryKey = [endpoint, { ...credentials, ...(params as object) }];
  const queryFn: QueryFunction<TData, DLQueryKey> = async ({ queryKey }) => {
    const [endpnt, prms] = queryKey;

    try {
      const response: unknown = await fetchWithHeaders({ method: 'GET', url: endpnt, params: prms });

      return unwrapApiResult<TData>(response);
    } catch (error) {
      window.dispatchEvent(new CustomEvent('reportErrorToFaro', { detail: { error, data: { method: 'GET', url: endpnt, params: prms } } }));
      throwError(error instanceof Error ? error.message : String(error));

      throw error;
    }
  };

  const { useErrorBoundary: optionsUseErrorBoundary, ...restOptions } = options ?? {};
  const sanitizedOptions = restOptions as Omit<UseQueryOptionsWithGenerics<TData>, 'useErrorBoundary'>;
  const { cacheTime, ...queryOptions } = sanitizedOptions as Omit<UseQueryOptionsWithGenerics<TData>, 'useErrorBoundary'> & {
    cacheTime?: number;
  };

  return useQuery<TData, DLAPIErrorType, TData, DLQueryKey>({
    queryKey: newQueryKey,
    queryFn,
    enabled: isEnabled,
    throwOnError: queryOptions.throwOnError ?? optionsUseErrorBoundary ?? useErrorBoundary,
    gcTime: queryOptions.gcTime ?? cacheTime,
    ...queryOptions,
  });
}
export function useInfiniteQueryWithAccessToken<TData = unknown>(
  [endpoint, params]: DLInfiniteQueryKey,
  options?: UseInfiniteQueryOptionsWithGenerics<TData>
) {
  const [sharedState] = useBridgeContext();

  const user = sharedState?.user;

  let credentials: object | undefined;

  if (!user) {
    credentials = {};
  } else if (user.isSwitchedUser) {
    credentials = { username: user.username, logged_in_access_token: user.accessToken };
  } else {
    credentials = { access_token: user.accessToken };
  }

  const newQueryKey: DLInfiniteQueryKey = [endpoint, { ...credentials, ...params }];
  const queryFn: QueryFunction<TData, DLInfiniteQueryKey, number> = async ({ queryKey, pageParam = 1 }) => {
    const [endpnt, prms] = queryKey;

    const newParams = {
      ...prms,
      page: pageParam,
    };

    window.pendo.track(mediasLoadedInfiniteScroll, {
      perPage: params.perPage,
      page: pageParam,
    });

    const response: unknown = await fetchWithHeaders({ method: 'GET', url: endpnt, params: newParams });

    return unwrapApiResult<TData>(response);
  };

  const { useErrorBoundary: optionsUseErrorBoundary, ...restOptions } = options ?? {};
  const sanitizedOptions = restOptions as Omit<UseInfiniteQueryOptionsWithGenerics<TData>, 'useErrorBoundary'>;
  const { cacheTime, ...infiniteOptions } = sanitizedOptions as Omit<UseInfiniteQueryOptionsWithGenerics<TData>, 'useErrorBoundary'> & {
    cacheTime?: number;
  };
  const initialPageParam = infiniteOptions.initialPageParam ?? 1;
  const getNextPageParam = infiniteOptions.getNextPageParam
    ? infiniteOptions.getNextPageParam
    : (lastPage: TData, allPages: TData[]) =>
        lastPage && Array.isArray(lastPage) && lastPage.length < (params.perPage || params.per_page || 0) ? undefined : allPages.length + 1;

  return useInfiniteQuery<TData, DLAPIErrorType, InfiniteData<TData>, DLInfiniteQueryKey, number>({
    ...infiniteOptions,
    queryKey: newQueryKey,
    queryFn,
    throwOnError: infiniteOptions.throwOnError ?? optionsUseErrorBoundary ?? false,
    gcTime: infiniteOptions.gcTime ?? cacheTime,
    initialPageParam,
    getNextPageParam,
  });
}

export function useMutationWithAccessToken<TData, TVariables>({
  method,
  endpoint,
  options,
  body = {},
  mutationFn,
}: UseMutationWithAccessTokenParamsType<TData, TVariables>) {
  const [
    {
      user: { accessToken },
    },
  ] = useBridgeContext();

  const defaultMutationFn: MutationFunction<TData, object> = async (variables: object) => {
    const newContent = { access_token: accessToken, ...variables };

    if (mutationFn) {
      return mutationFn(newContent as TVariables);
    }

    return fetchWithHeaders({ method, url: endpoint, params: newContent, body }).then((response: unknown) => unwrapApiResult<TData>(response));
  };

  return useMutation({ mutationFn: defaultMutationFn, ...(options as UseMutationOptionsWithGenerics<TData>) });
}
